import { GroupedCheckboxPicker } from '@statsbomb/kitbag-components';
import { ComponentProps } from 'react';
import { BaseOption, TranslatedColumnGroup } from '@/types/generic';
import { hasIs360 } from '@/utils/predicates';

type CheckboxGroup = ComponentProps<typeof GroupedCheckboxPicker>['groups'][number];
type CheckboxItem = CheckboxGroup['subGroups'][number]['items'][number];

const toCheckboxItem = (
  { value, label, optional }: BaseOption<string>,
  threeSixtyLabel: string,
  physicalLabel: string,
): CheckboxItem => ({
  value,
  label,
  ...(optional?.longLabel && { secondarySearchTerm: optional.longLabel }),
  ...(optional?.isCore === false && { isSecondary: true }),
  ...(optional?.tooltip && { tooltip: optional.tooltip }),
  ...(optional?.isDisabled && { isDisabled: true }),
  ...(hasIs360(optional) && optional.is360 && { indicator: { icon: 'ThreeSixty', text: threeSixtyLabel } }),
  ...(optional?.isPhysical && !optional?.is360 && { indicator: { icon: 'Tracking', text: physicalLabel } }),
});

/**
 * Applies a per-option transform across every leaf of the grouped shape, keeping the
 * group/sub-group structure intact. Lets consumers decorate options without re-deriving the shape.
 */
export const mapGroupOptions = (
  groups: TranslatedColumnGroup[],
  mapOption: (option: BaseOption<string>) => BaseOption<string>,
): TranslatedColumnGroup[] =>
  groups.map(group => ({
    ...group,
    options: group.options.map(subGroup => ({ ...subGroup, options: subGroup.options.map(mapOption) })),
  }));

/**
 * Formats our column groups into kitbag's 3-level CheckboxGroup model. Every group is already
 * normalised to sub-groups upstream, so a category with no sub-category arrives as a single
 * sub-group with an empty header, which renders as an unlabelled section.
 */
export const toCheckboxGroups = (
  groups: TranslatedColumnGroup[],
  threeSixtyLabel: string,
  physicalLabel: string,
): CheckboxGroup[] =>
  groups.map(group => ({
    id: group.header,
    label: group.header,
    subGroups: group.options.map(subGroup => ({
      id: `${group.header}-${subGroup.header}`,
      label: subGroup.header,
      items: subGroup.options.map(option => toCheckboxItem(option, threeSixtyLabel, physicalLabel)),
    })),
  }));
